/**
 * PanelGear Appwrite Client Service
 * 
 * Unified client for all Appwrite operations using native Teams for multi-tenancy.
 * 
 * Architecture:
 * - Teams = Organisations (one team per panel shop)
 * - Team Roles = User roles (admin, manager, receptionist, tradesperson, viewer)
 * - Document Permissions = Access control per document
 * - User Prefs = User settings/preferences
 * 
 * @version 2.0.0 - Teams-based architecture
 */

import {
  Client,
  Account,
  Databases,
  Storage,
  Functions,
  Teams,
  Presences,
  Realtime,
  Channel,
  ID,
  Query,
  Permission,
  Role,
  OAuthProvider
} from 'appwrite';
import { createHistoryMarkers, withHistoryMarkers, MARKERS_TABLE, HISTORY_DATABASE_ID } from '../lib/historyMarkers';
import { getOAuthProvider, setReturnTo } from '../lib/signInMethods';

// =====================================================
// CONFIGURATION
// =====================================================

const endpoint = process.env.REACT_APP_APPWRITE_ENDPOINT || 'http://localhost/v1';
const projectId = process.env.REACT_APP_APPWRITE_PROJECT || 'dev-flickquote';

if (!endpoint || !projectId) {
  console.warn('Appwrite configuration may be incomplete. Check REACT_APP_APPWRITE_ENDPOINT and REACT_APP_APPWRITE_PROJECT in .env');
}

// Create Appwrite client
const client = new Client()
  .setEndpoint(endpoint)
  .setProject(projectId);

// Service instances
export const account = new Account(client);
// Every database write in the app goes through this instance. Creates and
// updates of the records history captures also leave a "who edited" marker,
// at most one per record per user a minute — see lib/historyMarkers.js.
const rawDatabases = new Databases(client);
export const databases = withHistoryMarkers(
  rawDatabases,
  createHistoryMarkers({
    // Granted to the writer alone: Appwrite will not let a client grant a role
    // it does not hold, which is what makes the grant proof of who wrote it.
    write: (tableName, rowId, userId) => rawDatabases.createDocument(
      HISTORY_DATABASE_ID, MARKERS_TABLE, ID.unique(), { tableName, rowId }, [Permission.read(Role.user(userId))]
    ),
    currentUser: () => getCurrentUser(),
  })
);
export const storage = new Storage(client);
export const functions = new Functions(client);
export const teams = new Teams(client);
export const presences = new Presences(client);
// One Realtime instance for the whole app — it multiplexes every subscription
// onto a single socket and owns the reconnect/backoff. `client.subscribe()` is
// deprecated in SDK 26 in favour of this.
export const realtime = new Realtime(client);

// Re-export for convenience
export { client, Channel, ID, Query, Permission, Role };

// =====================================================
// CONSTANTS
// =====================================================

export const DATABASE_ID = 'flickquote';

export const COLLECTIONS = {
  CUSTOMERS: 'customers',
  CUSTOMER_VEHICLES: 'customer_vehicles',
  QUOTE_REQUESTS: 'quote_requests',
  QUOTE_REQUEST_IMAGES: 'quote_request_images',
  QUOTES: 'quotes',
  QUOTE_LINES: 'quote_lines',
  JOBS: 'jobs',
  JOB_TASKS: 'job_tasks',
  VEHICLE_DAMAGE: 'vehicle_damage',
  NOTES: 'notes',
  // Fleet — the shop's own vehicles (courtesy cars, company cars, work utes)
  // and their bookings. Deliberately separate from CUSTOMER_VEHICLES, which
  // holds customer-owned cars and requires a customerId.
  FLEET_VEHICLES: 'fleet_vehicles',
  FLEET_BOOKINGS: 'fleet_bookings',
  FLEET_VEHICLE_EVENTS: 'fleet_vehicle_events',
  // Customer organisation data
  CUSTOMER_ORGANISATIONS: 'customer_organisations',
  CUSTOMER_ORGANISATION_LINKS: 'customer_organisation_links',
  CUSTOMER_RELATIONSHIPS: 'customer_relationships',
  // Panel Quote integration cache
  PANEL_QUOTE_QUOTES: 'panel-quote-quotes',
  PANEL_QUOTE_STATUS: 'panel-quote-status',
  PANEL_QUOTE_CUSTOMERS: 'panel-quote-customers',
  PANEL_QUOTE_INSURER_MAP: 'panel-quote-insurer-map',
  // Public lookup data
  REGIONS: 'regions',
  INSURERS: 'insurers',
  INSURER_CONFIGS: 'insurer_configs',
  INSURER_LINE_ALLOWANCES: 'insurer_line_allowances',
  // The shop line sheet: platform standard lines (blank organisationId) that
  // every shop gets, plus each shop's own rows — its additions, and its edits or
  // disabling of a standard line. Row security, so a shop only ever sees the
  // standards and its own.
  ORG_LINE_ALLOWANCES: 'org_line_allowances',
  VEHICLE_MAKES_MODELS: 'vehicle_makes_models',
  // Staff list for User settings: one row per (organisation, user), edited
  // directly by owners; user-profile-sync carries edits across to Auth (#68).
  USER_PROFILES: 'user_profiles'
};

export const BUCKETS = {
  QUOTE_REQUEST_IMAGES: 'quote-request-images',
  DOCUMENTS: 'documents',
  REPAIR_GUIDELINES: 'repair-guidelines',
  // Public-read: anonymous customers load the shop's logo on the intake form.
  ORG_BRANDING: 'org-branding',
  // Condition photos, licence scans and signed loan agreements.
  FLEET_DOCUMENTS: 'fleet-documents'
};

export const FUNCTION_IDS = {
  CUSTOMER_RFQ_SUBMIT: 'customer-rfq-submit',
  GENERATE_QUOTE_FROM_DAMAGE: 'generate-quote-from-damage',
  PANEL_QUOTE_IMPORT: 'panel-quote-import',
   PANEL_QUOTE_LIST_QUOTES: 'panel-quote-list-quotes',
  PANEL_QUOTE_LIST_CUSTOMERS: 'panel-quote-list-customers',
  PANEL_QUOTE_TEST: 'panel-quote-test',
  CREDENTIAL_STORE: 'team-credential-store',
  USER_MANAGEMENT: 'user-management',
  AI_IMAGE_ANALYSIS: 'ai-image-analysis',
  ML_PART_DETECTION: 'ml-part-detection',
  ML_DAMAGE_DETECTION: 'ml-damage-detection',
  PANEL_QUOTE_UPDATE_QUOTE: 'panel-quote-update-quote',
  LICENCE: 'licence',
  ORG_SIGNUP: 'org-signup'
};

// Team roles matching the role hierarchy (for team memberships)
export const TEAM_ROLES = {
  OWNER: 'owner',
  MEMBER: 'member'
};

// User labels (Appwrite Auth labels for role-based access)
// These are set via the user-management function
export const USER_LABELS = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  RECEPTIONIST: 'receptionist',
  TRADESPERSON: 'tradesperson',
  VIEWER: 'viewer'
};

// All valid labels for validation
export const VALID_LABELS = ['admin', 'manager', 'receptionist', 'tradesperson', 'viewer'];

// Label hierarchy (higher number = more powerful)
// These correspond to USER_LABELS
const LABEL_POWER = {
  viewer: 1,
  tradesperson: 2,
  receptionist: 3,
  manager: 4,
  admin: 5
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Get the most powerful label from an array of labels
 */
export function getEffectiveLabel(labels) {
  if (!labels || labels.length === 0) return null;
  
  return labels.reduce((highest, label) => {
    const power = LABEL_POWER[label] || 0;
    const highestPower = LABEL_POWER[highest] || 0;
    return power > highestPower ? label : highest;
  }, labels[0]);
}

// Alias for backward compatibility
export const getEffectiveRole = getEffectiveLabel;

/**
 * Check if user has at least the required label level
 */
export function hasLabelLevel(userLabels, requiredLabel) {
  const effectiveLabel = getEffectiveLabel(userLabels);
  if (!effectiveLabel) return false;
  
  const userPower = LABEL_POWER[effectiveLabel] || 0;
  const requiredPower = LABEL_POWER[requiredLabel] || 0;
  
  return userPower >= requiredPower;
}

// Alias for backward compatibility
export const hasRoleLevel = hasLabelLevel;

/**
 * Build permissions array for a document based on team
 * 
 * Appwrite supports these permission formats:
 * - Role.team(teamId) - All team members
 * - Role.team(teamId, 'owner') - Team owners (the `owner` role Appwrite gives the team creator)
 * - Role.label('labelname') - Users with specific labels
 * - Role.user(userId) - Specific user
 * 
 * @param {string} teamId - The team ID
 * @param {object} options - Permission options
 */
export function buildPermissions(teamId, options = {}) {
  const {
    readAll = true, // All team members can read by default
    writeAll = true, // All team members can write by default (for collaboration)
    deleteAdminOnly = false // Only team admins (owners) can delete
  } = options;

  const permissions = [];

  // Read permissions - all team members
  if (readAll) {
    permissions.push(Permission.read(Role.team(teamId)));
  }

  // Update permissions - all team members for collaboration
  if (writeAll) {
    permissions.push(Permission.update(Role.team(teamId)));
  }

  // Delete permissions
  if (deleteAdminOnly) {
    // Only team owners can delete. `owner` is the role Appwrite actually
    // assigns (see org-signup.js) — `admin` is never assigned as a team role,
    // so Role.team(teamId, 'admin') would never match anyone.
    permissions.push(Permission.delete(Role.team(teamId, 'owner')));
  } else {
    // All team members can delete
    permissions.push(Permission.delete(Role.team(teamId)));
  }

  return permissions;
}

// =====================================================
// AUTHENTICATION & USER CONTEXT
// =====================================================

/**
 * The Appwrite provider for one of the app's sign-in providers. Throws for a
 * provider the app does not offer — see OAUTH_PROVIDERS in lib/signInMethods.
 */
function toOAuthProvider(provider) {
  const id = provider === 'azure' ? 'microsoft' : provider;
  if (!getOAuthProvider(id) || !Object.values(OAuthProvider).includes(id)) {
    throw new Error(`Unsupported OAuth provider: ${provider}`);
  }
  return id;
}

/**
 * Sign in with OAuth provider
 * @param {string} provider - an id from OAUTH_PROVIDERS, e.g. 'google' or 'microsoft'
 */
export async function signInWithOAuth(provider) {
  try {
    const oauthProvider = toOAuthProvider(provider);

    const successUrl = `${window.location.origin}/auth/callback`;
    // Failures come back through the callback page too, never straight to
    // /login: Appwrite appends what went wrong as `error`, and the callback
    // reads it and leaves for a clean /login, so a refresh doesn't replay it.
    // `failed` says which provider, so a refused one can be connected instead.
    const failureUrl = `${window.location.origin}/auth/callback?failed=${oauthProvider}`;

    account.createOAuth2Session({
      provider: oauthProvider,
      success: successUrl,
      failure: failureUrl
    });

    return { error: null };
  } catch (error) {
    console.error('OAuth error:', error);
    return { error };
  }
}

/**
 * Connect another OAuth provider to the signed-in account.
 *
 * The same Appwrite call as signing in, made while a session exists: Appwrite
 * then links the provider to the current account instead of looking for one by
 * email. Both outcomes come back through /auth/callback, which reports the
 * result and returns to `returnTo`.
 */
export function connectOAuthProvider(provider, returnTo) {
  const oauthProvider = toOAuthProvider(provider);
  setReturnTo(returnTo);

  const origin = window.location.origin;
  account.createOAuth2Session({
    provider: oauthProvider,
    success: `${origin}/auth/callback?connected=${oauthProvider}`,
    failure: `${origin}/auth/callback?connect_failed=${oauthProvider}`
  });
}

/** The OAuth providers linked to the signed-in account, e.g. ['google']. */
export async function listLinkedProviders() {
  const result = await account.listIdentities();
  return [...new Set((result.identities || []).map((identity) => identity.provider))];
}

/**
 * Email a one-time sign-in link. For an address with no account, following the
 * link creates one — signup then asks for the shop name, as it does after OAuth.
 */
export async function sendEmailSignInLink(email) {
  return account.createMagicURLToken({
    userId: ID.unique(),
    email: email.trim().toLowerCase(),
    url: `${window.location.origin}/auth/callback`
  });
}

/** Exchange the `userId` and `secret` from an emailed sign-in link for a session. */
export async function completeEmailSignIn(userId, secret) {
  return account.createSession({ userId, secret });
}


/**
 * Sign out current user
 */
export async function signOut() {
  try {
    await account.deleteSession('current');
    clearUserSession();
    return { error: null };
  } catch (error) {
    console.error('Sign out error:', error);
    return { error };
  }
}

/**
 * Get current session
 */
export async function getSession() {
  try {
    const session = await account.getSession('current');
    return { session, error: null };
  } catch (error) {
    return { session: null, error };
  }
}

/**
 * Clear the current user session
 */
export function clearUserSession() {
  localStorage.removeItem('selected_team_id');
  localStorage.removeItem('current_user');
}

/**
 * Get current authenticated user with team context
 * Uses Appwrite Teams for organisation and labels for roles.
 * Labels are stored on the Appwrite user and fetched via user-management function.
 */
export async function getCurrentUser() {
  try {
    // No special handling needed for dev mode - we have a real session now

    // Try to get cached user first
    const cachedUser = localStorage.getItem('current_user');
    if (cachedUser) {
      try {
        const parsed = JSON.parse(cachedUser);
        // Validate cache is still valid (has required fields)
        if (parsed.id && parsed.teamId) {
          return parsed;
        }
      } catch {
        // Invalid cache, continue
      }
    }

    // Get authenticated user
    const authUser = await account.get();
    
    // Get user's teams
    const teamsResponse = await teams.list();
    const userTeams = teamsResponse.teams;
    
    if (userTeams.length === 0) {
      // User has no teams - this is an error state for our app
      console.warn('User has no team memberships');
      return {
        id: authUser.$id,
        email: authUser.email,
        name: authUser.name,
        requires_team_setup: true,
        teams: []
      };
    }

    // Check if user has selected a team (for multi-team users)
    const selectedTeamId = localStorage.getItem('selected_team_id');
    let selectedTeam = userTeams[0]; // Default to first team
    
    if (selectedTeamId) {
      const found = userTeams.find(t => t.$id === selectedTeamId);
      if (found) selectedTeam = found;
    }
    
    // If user has multiple teams and none selected, prompt for selection
    if (userTeams.length > 1 && !selectedTeamId) {
      return {
        id: authUser.$id,
        email: authUser.email,
        name: authUser.name,
        requires_team_selection: true,
        teams: userTeams.map(t => ({
          id: t.$id,
          name: t.name
        }))
      };
    }

    // Get user's membership in selected team
    const membershipsResponse = await teams.listMemberships(selectedTeam.$id);
    const userMembership = membershipsResponse.memberships.find(
      m => m.userId === authUser.$id
    );

    if (!userMembership) {
      console.error('User membership not found in team');
      return null;
    }

    // Get user's labels (roles) via user-management function
    // This is now optional - team membership roles are the primary authorization mechanism
    let labels = [];
    try {
      const labelResponse = await functions.createExecution(
        FUNCTION_IDS.USER_MANAGEMENT,
        JSON.stringify({ action: 'get-current-user' }),
        false
      );
      if (labelResponse.responseBody) {
        const parsed = JSON.parse(labelResponse.responseBody);
        if (parsed.success && parsed.user?.labels) {
          labels = parsed.user.labels;
        }
      }
    } catch (e) {
      console.warn('Could not fetch user labels:', e.message);
      // Continue without labels - team membership roles are used for access control
      labels = [];
    }

    // Get user preferences (for custom settings)
    let prefs = {};
    try {
      prefs = await account.getPrefs();
    } catch (e) {
      // No prefs set yet
    }

    // Build user context object
    const currentUser = {
      // Auth info
      id: authUser.$id,
      email: authUser.email,
      name: authUser.name,
      phone: authUser.phone || '',
      
      // Team/org info
      teamId: selectedTeam.$id,
      teamName: selectedTeam.name,
      organisationId: selectedTeam.$id, // Normalized property name
      // Aliases for backward compatibility
      organisation_id: selectedTeam.$id,
      organisation: {
        $id: selectedTeam.$id,
        name: selectedTeam.name
      },
      
      // Labels (roles) - from Appwrite user labels
      labels: labels,
      effectiveLabel: getEffectiveLabel(labels),
      // Backward compatibility aliases
      roles: labels,
      effectiveRole: getEffectiveLabel(labels),
      
      // Team membership info
      membershipId: userMembership.$id,
      teamRoles: userMembership.roles, // Team roles (owner, member)
      isTeamOwner: userMembership.roles.includes('owner'),
      
      // User preferences
      prefs: prefs,
      
      // For backward compatibility - parse name into first/last
      firstName: prefs.firstName || authUser.name?.split(' ')[0] || '',
      lastName: prefs.lastName || authUser.name?.split(' ').slice(1).join(' ') || '',
      first_name: prefs.firstName || authUser.name?.split(' ')[0] || '',
      last_name: prefs.lastName || authUser.name?.split(' ').slice(1).join(' ') || '',
      isActive: true,
      
      // Available teams (for multi-org users)
      teams: userTeams.map(t => ({
        id: t.$id,
        name: t.name
      }))
    };

    // Cache the user
    localStorage.setItem('selected_team_id', selectedTeam.$id);
    localStorage.setItem('current_user', JSON.stringify(currentUser));

    return currentUser;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

/**
 * Select a team for multi-team users
 */
export function selectTeam(teamId) {
  localStorage.setItem('selected_team_id', teamId);
  localStorage.removeItem('current_user'); // Clear cache to force refresh
}

/**
 * Update user preferences
 */
export async function updateUserPrefs(prefs) {
  try {
    const updated = await account.updatePrefs(prefs);
    // Clear cache to force refresh
    localStorage.removeItem('current_user');
    return { prefs: updated, error: null };
  } catch (error) {
    console.error('Error updating user prefs:', error);
    return { prefs: null, error };
  }
}

// =====================================================
// TEAM MANAGEMENT
// =====================================================

/**
 * Get team members for current team
 */
export async function getTeamMembers() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const response = await teams.listMemberships(user.teamId);
  return response.memberships.map(m => ({
    id: m.$id,
    userId: m.userId,
    userName: m.userName,
    userEmail: m.userEmail,
    roles: m.roles,
    effectiveRole: getEffectiveRole(m.roles),
    joined: m.joined,
    confirm: m.confirm
  }));
}

/**
 * Invite a user to the team
 */
export async function inviteTeamMember(email, roles, name = null) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  // Only owners and admins can invite
  if (!hasRoleLevel(user.roles, 'admin')) {
    throw new Error('Insufficient permissions to invite team members');
  }

  const membership = await teams.createMembership(
    user.teamId,
    roles,
    email,
    undefined, // userId
    undefined, // phone
    `${window.location.origin}/auth/accept-invite`, // redirect URL
    name
  );

  return membership;
}

/**
 * Update team member roles
 */
export async function updateTeamMemberRoles(membershipId, roles) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  // Only owners can update roles
  if (!hasRoleLevel(user.roles, 'owner')) {
    throw new Error('Only team owners can update member roles');
  }

  const updated = await teams.updateMembership(
    user.teamId,
    membershipId,
    roles
  );

  return updated;
}

/**
 * Remove team member
 */
export async function removeTeamMember(membershipId) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  // Only owners can remove members
  if (!hasRoleLevel(user.roles, 'owner')) {
    throw new Error('Only team owners can remove members');
  }

  await teams.deleteMembership(user.teamId, membershipId);
}

/**
 * Get current team details
 */
export async function getCurrentTeam() {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const team = await teams.get(user.teamId);
  return team;
}

/**
 * Update team details
 */
export async function updateTeam(name) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  // Only org admins can update team: Appwrite team role `owner` or the
  // `admin` label — same rule as billing (see LicenceContext's isAdmin).
  // `user.roles` is the label array, which never contains 'owner', so this
  // must check isTeamOwner directly rather than going through hasRoleLevel.
  if (!user.isTeamOwner && !(user.labels || []).includes('admin')) {
    throw new Error('Insufficient permissions to update team');
  }

  const updated = await teams.updateName(user.teamId, name);
  // Clear cache
  localStorage.removeItem('current_user');
  return updated;
}

// =====================================================
// DATABASE HELPERS
// =====================================================

/**
 * Tables whose rows carry an `organisationId` and are read through the
 * browser's active organisation. Row permissions are `read(team:<orgId>)`, and
 * Appwrite grants a read when ANY of a user's teams matches, so a user who
 * belongs to two organisations is permitted to read both organisations' rows.
 * Permissions therefore cannot scope a list to the active organisation - the
 * query has to. org_line_allowances is deliberately absent: its platform
 * standard rows have a blank organisationId and are filtered in shopLineList.
 */
export const ORG_SCOPED_COLLECTIONS = new Set([
  COLLECTIONS.CUSTOMERS,
  COLLECTIONS.CUSTOMER_VEHICLES,
  COLLECTIONS.QUOTE_REQUESTS,
  COLLECTIONS.QUOTE_REQUEST_IMAGES,
  COLLECTIONS.QUOTES,
  COLLECTIONS.JOBS,
  COLLECTIONS.JOB_TASKS,
  COLLECTIONS.VEHICLE_DAMAGE,
  COLLECTIONS.NOTES,
  COLLECTIONS.FLEET_VEHICLES,
  COLLECTIONS.FLEET_BOOKINGS,
  COLLECTIONS.FLEET_VEHICLE_EVENTS,
  COLLECTIONS.CUSTOMER_ORGANISATIONS,
  COLLECTIONS.CUSTOMER_ORGANISATION_LINKS,
  COLLECTIONS.CUSTOMER_RELATIONSHIPS,
  COLLECTIONS.PANEL_QUOTE_QUOTES,
  COLLECTIONS.PANEL_QUOTE_STATUS,
  COLLECTIONS.PANEL_QUOTE_CUSTOMERS,
  COLLECTIONS.PANEL_QUOTE_INSURER_MAP,
]);

/** The queries for a list, with the active organisation added when the table is org-scoped. */
export function scopeQueries(collectionId, teamId, queries = []) {
  if (!ORG_SCOPED_COLLECTIONS.has(collectionId)) return queries;
  return [Query.equal('organisationId', teamId), ...queries];
}

/**
 * List documents, scoped to the active organisation (see ORG_SCOPED_COLLECTIONS).
 */
export async function listDocuments(collectionId, queries = []) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  const response = await databases.listDocuments(
    DATABASE_ID,
    collectionId,
    scopeQueries(collectionId, user.teamId, queries)
  );

  return response;
}

/**
 * Get a single document
 */
export async function getDocument(collectionId, documentId) {
  return await databases.getDocument(
    DATABASE_ID,
    collectionId,
    documentId
  );
}

/**
 * Create a document with team permissions
 */
export async function createDocument(collectionId, data, documentId = null, permissionOptions = {}) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  // Build permissions based on team
  const permissions = buildPermissions(user.teamId, permissionOptions);

  return await databases.createDocument(
    DATABASE_ID,
    collectionId,
    documentId || ID.unique(),
    {
      ...data,
      // Include teamId/organisationId in the document data for querying
      organisationId: user.teamId
    },
    permissions
  );
}

/**
 * Update a document
 */
export async function updateDocument(collectionId, documentId, data) {
  return await databases.updateDocument(
    DATABASE_ID,
    collectionId,
    documentId,
    data
  );
}

/**
 * Delete a document
 */
export async function deleteDocument(collectionId, documentId) {
  return await databases.deleteDocument(
    DATABASE_ID,
    collectionId,
    documentId
  );
}

// =====================================================
// STORAGE HELPERS
// =====================================================

/**
 * Upload a file with team permissions
 */
export async function uploadFile(bucketId, file, fileId = null) {
  const user = await getCurrentUser();
  if (!user?.teamId) throw new Error('No team selected');

  // Scope to the uploader's org team. Appwrite blocks privilege escalation on
  // create, so we may only grant roles the uploader holds: every member holds
  // Role.team(teamId), but the `admin` team role is never assigned to members —
  // granting it here would make the upload fail permission validation.
  const permissions = [
    Permission.read(Role.team(user.teamId)),
    Permission.update(Role.team(user.teamId)),
    Permission.delete(Role.team(user.teamId))
  ];

  return await storage.createFile(
    bucketId,
    fileId || ID.unique(),
    file,
    permissions
  );
}

/**
 * Get file preview URL
 */
export function getFilePreview(bucketId, fileId, width = null, height = null) {
  let url = `${endpoint}/storage/buckets/${bucketId}/files/${fileId}/preview?project=${projectId}`;
  if (width) url += `&width=${width}`;
  if (height) url += `&height=${height}`;
  return url;
}

/**
 * Get file view URL — serves the file as-is, unlike /preview which rasterises
 * and cannot handle SVG. Use this for logos, which are often SVG.
 */
export function getFileViewUrl(bucketId, fileId) {
  return `${endpoint}/storage/buckets/${bucketId}/files/${fileId}/view?project=${projectId}`;
}

/**
 * Get file download URL
 */
export function getFileDownload(bucketId, fileId) {
  return `${endpoint}/storage/buckets/${bucketId}/files/${fileId}/download?project=${projectId}`;
}

/**
 * Fetch a file's text content (uses authenticated request for protected buckets)
 */
export async function getFileText(bucketId, fileId) {
  const url = getFileDownload(bucketId, fileId);
  const response = await fetch(url, { credentials: 'include' });

  if (!response.ok) {
    throw new Error(`Failed to fetch file ${fileId} from ${bucketId}: ${response.status}`);
  }

  return await response.text();
}

/**
 * Delete a file
 */
export async function deleteFile(bucketId, fileId) {
  return await storage.deleteFile(bucketId, fileId);
}

/**
 * Get file as data URL (blob URL) for use in canvas/Konva
 * This fetches the file via the SDK URL and converts to blob URL
 * which avoids CORS issues when loading images into canvas
 */
export async function getFileAsDataUrl(bucketId, fileId) {
  // In Web SDK, getFileView returns a URL object, not the data
  // We need to fetch the actual data using that URL
  const url = storage.getFileView(bucketId, fileId);
  
  // Fetch the image data - this goes through same-origin since SDK builds correct URL
  const response = await fetch(url, {
    credentials: 'include' // Include cookies for auth
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
  }
  
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

// =====================================================
// FUNCTION HELPERS
// =====================================================

/**
 * Execute a function synchronously (waits for result)
 */
export async function executeFunction(functionId, body = '', path = '/', method = 'POST') {
  const execution = await functions.createExecution(
    functionId,
    body,
    false, // async = false means synchronous
    path,
    method
  );

  if (execution.responseStatusCode !== 200) {
    const response = JSON.parse(execution.responseBody || '{}');
    // Functions report failures as either a plain `error` string or an Appwrite
    // style error object; losing the string one leaves the user with nothing
    // but "Function execution failed".
    const message = typeof response.error === 'string' ? response.error : response.error?.message;
    throw new Error(message || 'Function execution failed');
  }

  return JSON.parse(execution.responseBody);
}

/**
 * Execute a function asynchronously (fire and forget, doesn't wait for result)
 * Use this for long-running functions that exceed the 30s sync timeout
 */
export async function executeFunctionAsync(functionId, body = '', path = '/', method = 'POST') {
  const execution = await functions.createExecution(
    functionId,
    body,
    true, // async = true means asynchronous (fire and forget)
    path,
    method
  );

  // For async executions, we just return the execution info
  // The actual result will be available later via execution logs
  return {
    executionId: execution.$id,
    status: execution.status,
    functionId: execution.functionId
  };
}

// =====================================================
// REALTIME SUBSCRIPTIONS
// =====================================================

/**
 * Subscribe to document changes
 */
export function subscribeToDocument(collectionId, documentId, callback) {
  const channel = `databases.${DATABASE_ID}.collections.${collectionId}.documents.${documentId}`;
  
  try {
    const unsubscribe = client.subscribe(channel, (response) => {
      callback(response);
    });
    
    return unsubscribe;
  } catch (error) {
    console.warn('[Realtime] Subscription failed:', error.message);
    // Return a no-op unsubscribe function if subscription fails
    return () => {};
  }
}

/**
 * Subscribe to collection changes
 */
export function subscribeToCollection(collectionId, callback) {
  const channel = `databases.${DATABASE_ID}.collections.${collectionId}.documents`;
  // A user in several organisations is sent events for all of them; drop the
  // ones from an organisation other than the active one.
  let teamId = null;
  try { teamId = localStorage.getItem('selected_team_id'); } catch (e) { /* unavailable */ }
  return client.subscribe(channel, (response) => {
    const eventOrg = response?.payload?.organisationId;
    if (ORG_SCOPED_COLLECTIONS.has(collectionId) && teamId && eventOrg && eventOrg !== teamId) return;
    callback(response);
  });
}

/**
 * Subscribe to presence changes (online status, field-editing indicators).
 * Async — the Realtime service resolves once the subscription is registered.
 * Resolves to { unsubscribe, update, close }.
 */
export function subscribeToPresences(callback) {
  return realtime.subscribe(Channel.presences(), callback);
}

// =====================================================
// LOOKUP DATA (Public collections)
// =====================================================

/**
 * Get regions (public lookup data)
 */
export async function getRegions() {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.REGIONS,
    [Query.orderAsc('region'), Query.limit(100)]
  );
  return response.documents;
}

/**
 * Get insurers (public lookup data)
 */
export async function getInsurers() {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.INSURERS,
    [Query.orderAsc('name'), Query.limit(100)]
  );
  return response.documents;
}

/**
 * Get vehicle makes and models (public lookup data)
 * Returns an object keyed by make name, with arrays of model names as values
 * Also includes "Other" as a special make option for non-standard makes
 */
export async function getMakesModels() {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.VEHICLE_MAKES_MODELS,
    [Query.limit(5000)]
  );
  
  // Group models by make
  const makeModelMap = {};
  
  response.documents.forEach(doc => {
    const make = doc.make;
    const model = doc.model;
    
    if (!makeModelMap[make]) {
      makeModelMap[make] = [];
    }
    if (model && !makeModelMap[make].includes(model)) {
      makeModelMap[make].push(model);
    }
  });
  
  // Sort models within each make
  Object.keys(makeModelMap).forEach(make => {
    makeModelMap[make].sort();
    // Add "Other" as an option for each make
    if (!makeModelMap[make].includes('Other')) {
      makeModelMap[make].push('Other');
    }
  });
  
  // Add "Other" as a special make option
  if (!makeModelMap['Other']) {
    makeModelMap['Other'] = ['Other'];
  }
  
  // Attach raw records for classification lookups (bodyType, isLuxury, etc.)
  makeModelMap._records = response.documents;
  
  return makeModelMap;
}

// =====================================================
// EXPORT DEFAULT CLIENT OBJECT
// =====================================================

const appwrite = {
  // Core instances
  client,
  account,
  databases,
  storage,
  functions,
  teams,
  presences,
  realtime,

  // Helpers
  ID,
  Query,
  Permission,
  Role,
  
  // Constants
  DATABASE_ID,
  COLLECTIONS,
  BUCKETS,
  FUNCTION_IDS,
  TEAM_ROLES,
  USER_LABELS,
  VALID_LABELS,
  
  // Auth functions
  signInWithOAuth,
  connectOAuthProvider,
  listLinkedProviders,
  sendEmailSignInLink,
  completeEmailSignIn,
  signOut,
  getSession,
  getCurrentUser,
  selectTeam,
  clearUserSession,
  updateUserPrefs,
  
  // Label/Role helpers
  getEffectiveLabel,
  getEffectiveRole, // alias for backward compatibility
  hasLabelLevel,
  hasRoleLevel, // alias for backward compatibility
  buildPermissions,
  
  // Team management
  getTeamMembers,
  inviteTeamMember,
  updateTeamMemberRoles,
  removeTeamMember,
  getCurrentTeam,
  updateTeam,
  
  // Database operations
  listDocuments,
  getDocument,
  createDocument,
  updateDocument,
  deleteDocument,
  
  // Storage operations
  uploadFile,
  getFilePreview,
  getFileViewUrl,
  getFileDownload,
  getFileText,
  getFileAsDataUrl,
  deleteFile,
  
  // Function execution
  executeFunction,
  executeFunctionAsync,
  
  // Realtime
  subscribeToDocument,
  subscribeToCollection,
  subscribeToPresences,

  // Lookup data
  getRegions,
  getInsurers,
  getMakesModels
};

export default appwrite;
